import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Skeleton,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import axios from "axios";
import Cookies from "universal-cookie";
import BankTransferPaymentDialog, {
  BankTransferPaymentDetails,
} from "../AgilityClubs/BankTransferPaymentDialog";
import {
  buildCompactInvoiceReference,
  sumInvoiceAmounts,
} from "../AgilityClubs/invoicePaymentUtils";
import ClubMembershipCard from "./ClubMembershipCard";
import ClubPicker from "./ClubPicker";
import type { BookOneOffSessionResponse } from "../AgilityClubs/types";
import type { ClubMembership, Invoice } from "./types";

const fontFamily =
  "'Fredoka', 'Nunito', 'Comfortaa', 'Quicksand', sans-serif";

const ClubMemberships: React.FC = () => {
  const [memberships, setMemberships] = useState<ClubMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
  const [paymentDetails, setPaymentDetails] =
    useState<BankTransferPaymentDetails | null>(null);

  useEffect(() => {
    getMemberships();
  }, []);

  useEffect(() => {
    if (memberships.length === 1) {
      setSelectedClubId(memberships[0].id);
      return;
    }

    if (
      selectedClubId != null &&
      !memberships.some((club) => club.id === selectedClubId)
    ) {
      setSelectedClubId(null);
    }
  }, [memberships, selectedClubId]);

  const selectedClub = useMemo(
    () => memberships.find((club) => club.id === selectedClubId) ?? null,
    [memberships, selectedClubId],
  );

  const showClubPicker = memberships.length > 1 && selectedClubId == null;

  const getMemberships = async () => {
    try {
      setLoading(true);
      const token = new Cookies().get("token");
      if (!token) {
        setMemberships([]);
        setError("Please log in to view your club memberships.");
        return;
      }

      const response = await axios.get(
        "https://api.easyagility.co.uk/my-memberships",
        { headers: { Authorization: "Bearer " + token } },
      );
      setMemberships(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      console.error("Error fetching memberships:", err);
      setError("Failed to load club memberships. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNowClick = (invoices: Invoice[]) => {
    if (invoices.length === 0) return;
    setPaymentDetails({
      paymentReference: buildCompactInvoiceReference(
        invoices.map((inv) => inv.id),
      ),
      amount: sumInvoiceAmounts(invoices),
    });
  };

  const handleSessionBooked = (result: BookOneOffSessionResponse) => {
    if (result.paid) {
      getMemberships();
      return;
    }
    setPaymentDetails({
      paymentReference: result.payment_reference,
      amount: result.amount,
    });
  };

  const handleClosePaymentDialog = () => {
    setPaymentDetails(null);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rectangular" height={400} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (memberships.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h2" sx={{ mb: 2, fontSize: "3rem" }}>
            🐕
          </Typography>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontFamily,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontSize: { xs: "1.75rem", md: "2rem" },
            }}
          >
            No Clubs Yet!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontFamily }}>
            You're not a member of any agility clubs yet. Join one to start
            training with your furry friend! 🎾
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: "linear-gradient(45deg, #4ECDC4 30%, #44A08D 90%)",
              fontWeight: 700,
              fontFamily,
              px: 4,
              py: 1.5,
              borderRadius: 3,
              boxShadow: "0 4px 12px rgba(78, 205, 196, 0.4)",
              "&:hover": {
                background: "linear-gradient(45deg, #44A08D 30%, #4ECDC4 90%)",
                transform: "scale(1.05)",
              },
            }}
          >
            Find Clubs Near You
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h3"
          gutterBottom
          sx={{
            fontWeight: 800,
            fontFamily,
            background: "linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          🐾 My Agility Clubs 🐾
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ fontWeight: 500, fontFamily }}
        >
          {showClubPicker
            ? "Choose a club to view your training, invoices, and bookings."
            : "Your paw-some training community!"}
        </Typography>
      </Box>

      {showClubPicker ? (
        <ClubPicker
          memberships={memberships}
          onSelect={setSelectedClubId}
        />
      ) : (
        selectedClub && (
          <Box>
            {memberships.length > 1 && (
              <Button
                startIcon={<ArrowBack />}
                onClick={() => setSelectedClubId(null)}
                sx={{
                  mb: 2,
                  fontWeight: 700,
                  fontFamily,
                  color: "#667eea",
                }}
              >
                All clubs
              </Button>
            )}
            <ClubMembershipCard
              club={selectedClub}
              onPayNowClick={handlePayNowClick}
              onSessionBooked={handleSessionBooked}
              onRefreshMemberships={getMemberships}
            />
          </Box>
        )
      )}

      <BankTransferPaymentDialog
        open={paymentDetails != null}
        details={paymentDetails}
        onClose={handleClosePaymentDialog}
      />
    </Container>
  );
};

export default ClubMemberships;
